import React, { useEffect } from 'react';
import 'react-pdf/dist/esm/Page/PageCanvas';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.vite';
import Sidebar from '../Sidebar/Sidebar';

import Loader from 'monday-ui-react-core/dist/Loader';
import { useContext } from 'react';
import { useDrop } from 'react-dnd';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { FileContext } from '../../context/FileContext';
import { CustomDragLayer } from '../CustomDragLayer';
import DragabbleChips from '../DraggableChips';
import ControlToolbar from '../Header/ControlToolbar';
import CheckboxPreviewOverlayChip from '../PDFOverlayChips/Checkbox/Preview';
import SignatureAndInitialsPreviewChip from '../PDFOverlayChips/SignatureAndInitials/Preview';
import TextBoxPreviewOverlayChip from '../PDFOverlayChips/TextBox/Preview';

import { PDFDocument } from 'pdf-lib';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';
import APP_CONFIG from '../../config';
import { useEnvelope } from '../../hooks/useEnvelope';
import {
  generateFilePreview,
  generateRealtimeFilePreview,
} from '../../services/file';
import ExpandOnHoverButton from '../CustomButton/ExpandOnHoverButton';
import { DeleteIcon, FontPlusIcon } from '../DraggableChips/optionsIcons';
import { LineItemsModal } from '../LineItems';
import DatePickerPreview from '../PDFOverlayChips/DatePicker/Preview';
import DropdownPreviewOverlayChip from '../PDFOverlayChips/Dropdown/Preview';
import { SignatureOrderModal } from '../SignatureOrder';
import CkEditorConfirmationModal from './Modal/CkEditorConfirmationModal';
import DeletePageConfirmationModal from './Modal/DeletePageConfirmation';
import SwitchPageLayer from './SwitchPageLayer';
import './css.css';
import { getDropdownLabels } from './useGetDropdownLabels';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const AddRemovePageButton = styled.button`
  position: absolute;
  ${({ position }) => (position === 'top' ? 'top: 0;' : 'bottom: 0;')};
  right: 0;
  cursor: pointer;
  opacity: 0;
  margin: 0.75rem;
  background-color: white;
  color: ${({ color }) => color};
  border: ${({ color }) => `1px solid ${color}`};
  border-radius: 4px;
  padding: 2px 6px 2px 6px;
  font-size: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;

  &:hover {
    opacity: 1;
  }
`;

const PDFPageOverlay = styled.div`
  pointer-events: none;
  position: absolute;
  top: ${({ page, pageAboveHeight, scale }) =>
    pageAboveHeight + (16 / scale) * page + 'px'};
  width: ${({ width }) => (width ? width + 'px' : '100%')};
  height: ${({ height }) => (height ? height + 'px' : '100%')};
  z-index: 2;
  margin-left: ${({ scale }) => 16 / scale + 'px'};
`;

const RelativeContainer = styled.div`
  position: relative;
  width: ${({ isPreview }) => (isPreview ? '100vw' : '80vw')};
  min-height: 100%;
  overflow-y: auto;
`;

const DocumentOverlay = styled.div`
  pointer-events: none;
  position: absolute;
  top: ${({ displaceTop }) => displaceTop + 16 + 'px'};
  left: 0;
  scale: ${({ scalingFactor }) => scalingFactor};
`;

const ScaledContainer = styled.div`
  scale: 1;
  height: 100%;
  @media only screen and (max-width: 768px) {
    overflow-x: hidden;
  }
`;

const CustomButtonWrapper = styled.div`
  padding: 5px 8px;
  width: max-content;
  color: white;
  border-radius: 4px;
  background-color: ${({ buttonColor }) => buttonColor || '#0073ea'};
  font-weight: 600;
  font-size: 14px;
  position: absolute;
  top: ${({ y }) => y + 'px'};
  left: ${({ x }) => x + 'px'};
  line-height: 14px;
  pointer-events: auto;
`;

const PreviewPDF = ({
  isPreview,
  isAdhoc = false,
  templateId,
  fromTemplateGallery = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    addedFormFields,
    addPlaceHolder,
    documentRef,
    onDocumentLoadSuccess,
    numPages,
    envelopeId,
    previewFile,
    handleRenderSuccess,
    hasPageLoaded,
    updateFormFields,
    currentSelectedItem,
    pageScale,
    fileId,
    itemId,
    showLineItemModal,
    showSignatureOrderModal,
    setShowSignateOrderModal,
    pdfDimensions,
    pageDeleteIndex,
    setPageDeleteIndex,
    pagesAddedByUser,
    pdfIndividualPageDetails,
    showCKEditorEntryConfirmation,
    setShowCKEditorEntryConfirmation,
    setPageScale,
    initialFields,
    isTemplate,
    removePlaceholder,
  } = useContext(FileContext);

  const {
    activeFile,
    activeFileType,
    addPageInEnvelopePDF,
    fileLinkTop,
    fileLinkBottom,
    envelopeData,
  } = useEnvelope();

  const isGeneratePDFPage = useMatch('/generate-pdf/*');
  const sessionToken = localStorage.getItem('sessionToken');

  const { data: generatedFilePreview, isLoading } = useQuery(
    ['get-file-preview', fileId, itemId, envelopeId],
    () =>
      isTemplate?.status || isPreview
        ? generateRealtimeFilePreview(
            envelopeId,
            envelopeData.activeFileId,
            itemId,
            addedFormFields,
          )
        : generateFilePreview({ fileId, itemId, envelopeId }),
    {
      enabled: !!isPreview && !!itemId && !!fileId && !!sessionToken,
      refetchOnWindowFocus: false,
    },
  );

  const scalingFactor = Number(pageScale.value);
  const overlayScalingFactor =
    pdfDimensions.scale < Number(pageScale.value)
      ? pdfDimensions.scale
      : Number(pageScale.value);

  useEffect(() => {
    async function getPDFScale(file) {
      const pdfDoc = await PDFDocument.load(file);
      const pages = pdfDoc.getPages();
      const largestWidth = Math.max(
        ...pages.map((x) => {
          const dimension = x.getSize();
          const angle = x.getRotation().angle;
          let updatedWidth = dimension.width;
          if (angle === 90 || angle === 270) {
            updatedWidth = dimension.height;
          }
          return updatedWidth;
        }),
      );
      const deviceWidth = window.innerWidth * 0.8; // width of container where pdf is loaded
      const calculatedScale = deviceWidth / largestWidth;
      const formattedScale = Math.floor(calculatedScale * 10) / 10;
      if (formattedScale < 1.5) {
        const scale = {
          value: formattedScale.toString(),
          label: `${formattedScale * 100}%`,
        };
        setPageScale(scale);
      }
    }
    if (activeFile) {
      getPDFScale(activeFile);
    }
  }, [activeFile]);

  // initial drop
  const [, drop] = useDrop({
    accept: 'Column',
    drop: (item, monitor) => {
      const offset = monitor.getSourceClientOffset();
      if (offset && documentRef.current) {
        const rect = documentRef.current.getBoundingClientRect();
        //NOTE: here rect top is header and control toolbar section and 16 is the initial gap after controltoolbar to start of pdf
        addPlaceHolder({
          item,
          x: offset.x - rect.left - 16, //margin on every pages
          y: offset.y - rect.top - 16 + documentRef.current?.scrollTop, //margin on every pages
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // while dragging chips
  const [, dropChips] = useDrop({
    accept: 'Chips',
    drop: (item, monitor) => {
      const offset = monitor.getSourceClientOffset();
      if (offset && documentRef.current) {
        const rect = documentRef.current.getBoundingClientRect();
        //NOTE: here rect top is header and control toolbar section and 16 is the initial gap after controltoolbar to start of pdf
        addPlaceHolder({
          item,
          x: offset.x - rect.left - 16, //margin on every pages
          y: offset.y - rect.top - 16 + documentRef.current?.scrollTop, //margin on every pages
          update: true,
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const attachRef = (el) => {
    drop(el);
    dropChips(el);
  };

  const handleItemClick = (index) => {
    addedFormFields.forEach((item) => (item.isActive = false));
    if (index > -1) {
      addedFormFields[index] = {
        ...addedFormFields[index],
        isActive: !addedFormFields[index]?.isActive,
      };
      currentSelectedItem.current = index;
    }
    updateFormFields([...addedFormFields]);
  };

  const handleItemResize = (index, value) => {};

  const selectedItem = addedFormFields[currentSelectedItem.current];

  const queryClient = useQueryClient();

  const queryData = queryClient.getQueryData(['get-columns', itemId, fileId]);

  // const labels = useDropdownLabels({
  //   boardColumns: queryData?.data?.items?.[0]?.board?.columns || [],
  //   targetColumnId: selectedItem?.column?.value || null,
  // });

  const getPDFDocumentContainerHeight = () => {
    const styles = { height: '100%' };
    if (!isPreview && currentSelectedItem.current !== null) {
      if (selectedItem?.itemId === 'checkbox') {
        styles.height = 'calc(100% - 38px)';
      } else {
        styles.height = 'calc(100% - 56px)';
      }
    }
    return styles;
  };

  function sumHeightsAboveIndex(heights, pageIndex) {
    return heights?.reduce((acc, item, index) => {
      if (index < pageIndex) {
        return acc + item.height;
      }
      return acc;
    }, 0);
  }

  const extractIdAndCoordinates = (fields) => {
    return fields.map((field) => ({
      id: field.id,
      placeholder: field.placeholder,
    }));
  };

  const navigateToWordEditor = () =>
    navigate({
      pathname: `/${
        fromTemplateGallery ? templateId : envelopeId
      }/${fileId}/${itemId}/word-editor/`,
      search: isAdhoc
        ? `isAdhoc=true&fromTemplateGallery=${fromTemplateGallery}`
        : `fromTemplateGallery=${fromTemplateGallery}`,
    });

  function openCKEditor() {
    const clonedAddedFields = extractIdAndCoordinates(addedFormFields);
    const clonedInitialFields = extractIdAndCoordinates(
      initialFields?.find((x) => x.fileId === fileId)?.fields || [],
    );
    if (
      addedFormFields.length &&
      JSON.stringify(clonedAddedFields) !== JSON.stringify(clonedInitialFields)
    ) {
      setShowCKEditorEntryConfirmation(true);
    } else {
      navigateToWordEditor();
    }
  }

  const isWordFile = ['docx', 'doc'].includes(activeFileType);

  return (
    <div className="pdf__viewer">
      {!isPreview ? <Sidebar isGeneratePDFPage={isGeneratePDFPage} /> : null}
      <RelativeContainer isPreview={isPreview}>
        {showLineItemModal && (
          <LineItemsModal fileId={fileId} itemId={itemId} />
        )}
        {showSignatureOrderModal && (
          <SignatureOrderModal closeModal={setShowSignateOrderModal} />
        )}
        {!!pageDeleteIndex && <DeletePageConfirmationModal />}
        {!!showCKEditorEntryConfirmation && (
          <CkEditorConfirmationModal
            type={isAdhoc ? 'adhoc' : 'template'}
            fromTemplateGallery={fromTemplateGallery}
            templateId={templateId}
          />
        )}
        <div className="preview_portion">
          <ControlToolbar
            isPreview={isPreview}
            isGeneratePDFPage={isGeneratePDFPage}
          />

          <div
            className="preview-pdf_document__container"
            ref={attachRef}
            style={getPDFDocumentContainerHeight()}
          >
            {/* {!isPreview && ['docx', 'doc'].includes(activeFileType) ? (
              <div className="word_editor_trigger_container">
                <ExpandOnHoverButton handleClick={openCKEditor} />
              </div>
            ) : null} */}

            <ScaledContainer
              ref={documentRef}
              className="pdf__document position-relative"
            >
              {fileLinkTop &&
              !isPreview &&
              !isTemplate?.status &&
              pdfIndividualPageDetails?.[0]?.width ? (
                <SwitchPageLayer
                  scalingFactor={overlayScalingFactor}
                  width={
                    pdfIndividualPageDetails?.[0]?.width * overlayScalingFactor
                  }
                  file={fileLinkTop}
                ></SwitchPageLayer>
              ) : null}
              {/* pdf rendering in the background */}

              {!isPreview && !activeFile ? (
                <div className="loader">
                  <Loader size={Loader.sizes.LARGE} />
                </div>
              ) : null}

              {isPreview && isLoading ? (
                <div className="loader">
                  <Loader size={Loader.sizes.LARGE} />
                </div>
              ) : null}

              {!isPreview && activeFile ? (
                <Document
                  file={activeFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div style={{ color: 'black', border: '1px solid red' }}>
                      {t('Loading')}
                    </div>
                  }
                  onLoadError={(err) => console.log(err)}
                  renderMode="canvas"
                  externalLinkTarget="_blank"
                  externalLinkRel="_blank"
                >
                  {Array.from(new Array(numPages), (el, pageIndex) => (
                    <Page
                      onRenderSuccess={handleRenderSuccess}
                      key={`page_${pageIndex + 1}`}
                      pageNumber={pageIndex + 1}
                      loading={<div>{t('Loading')}</div>}
                      renderTextLayer={false}
                      renderAnnotationLayer={true}
                      externalLinkTarget="_blank"
                      scale={scalingFactor}
                    ></Page>
                  ))}
                </Document>
              ) : null}

              {isPreview &&
              !isLoading &&
              (previewFile || generatedFilePreview?.data?.file) ? (
                <Document
                  file={generatedFilePreview?.data?.file || previewFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div style={{ color: 'black', border: '1px solid red' }}>
                      {t('Loading')}
                    </div>
                  }
                  onLoadError={(err) => console.log(err)}
                  renderMode="canvas"
                  externalLinkTarget="_blank"
                  externalLinkRel="_blank"
                >
                  {Array.from(new Array(numPages), (el, pageIndex) => (
                    <Page
                      onRenderSuccess={handleRenderSuccess}
                      key={`page_${pageIndex + 1}`}
                      pageNumber={pageIndex + 1}
                      loading={<div>{t('Loading')}</div>}
                      renderTextLayer={false}
                      renderAnnotationLayer={true}
                      externalLinkTarget="_blank"
                      scale={scalingFactor}
                    ></Page>
                  ))}
                </Document>
              ) : null}

              {/* absolute class overlay for all the draggable components */}
              <DocumentOverlay
                displaceTop={
                  fileLinkTop && !isPreview ? 85 * overlayScalingFactor : 0
                }
                scalingFactor={overlayScalingFactor}
              >
                <CustomDragLayer />
                {Array.from(new Array(numPages), (el, pageIndex) => (
                  <PDFPageOverlay
                    key={pageIndex}
                    height={pdfIndividualPageDetails?.[pageIndex]?.height}
                    width={pdfIndividualPageDetails?.[pageIndex]?.width}
                    page={pageIndex}
                    pageAboveHeight={
                      pageIndex > 0
                        ? sumHeightsAboveIndex(
                            pdfIndividualPageDetails,
                            pageIndex,
                          )
                        : 0
                    }
                    scale={overlayScalingFactor}
                  >
                    {!isAdhoc && !isWordFile ? (
                      <>
                        <AddRemovePageButton
                          position="bottom"
                          color="green"
                          onClick={() => addPageInEnvelopePDF(pageIndex + 1)}
                        >
                          <FontPlusIcon color="green" />
                          Add New Page
                        </AddRemovePageButton>

                        {pageIndex > 0 &&
                        pagesAddedByUser.includes(pageIndex) ? (
                          <AddRemovePageButton
                            position="top"
                            color="#d83a52"
                            onClick={() => setPageDeleteIndex(pageIndex)}
                          >
                            <DeleteIcon color="#d83a52" />
                            Delete This Page
                          </AddRemovePageButton>
                        ) : null}
                      </>
                    ) : null}

                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      {!isPreview &&
                        hasPageLoaded &&
                        !isLoading &&
                        addedFormFields.map(
                          (item, index) =>
                            item.formField.pageNumber === pageIndex + 1 && (
                              <DragabbleChips
                                item={item}
                                onItemClick={() => handleItemClick(index)}
                                onItemResize={(size) =>
                                  handleItemResize(index, size)
                                }
                                mondayBoardValues={queryData}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                documentRef={documentRef}
                                handleRemovePlaceholder={removePlaceholder}
                              />
                            ),
                        )}

                      {isPreview &&
                        addedFormFields.map(
                          (item, index) =>
                            Object.keys(APP_CONFIG.form_fields.standard_fields)
                              .map(
                                (obj) =>
                                  APP_CONFIG.form_fields.standard_fields[obj]
                                    .id,
                              )
                              .includes(item.itemId) &&
                            item.formField.pageNumber === pageIndex + 1 &&
                            (item.itemId ===
                            APP_CONFIG.form_fields.standard_fields.checkbox
                              .id ? (
                              <CheckboxPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.standard_fields.textBox
                                .id ? (
                              <TextBoxPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.standard_fields.dropdown
                                .id ? (
                              <DropdownPreviewOverlayChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                options={getDropdownLabels({
                                  boardColumns:
                                    queryData?.data?.items?.[0]?.board
                                      ?.columns || [],
                                  targetColumnId: item?.column?.value || null,
                                })}
                                showColor
                              />
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.payment.id ? (
                              <CustomButtonWrapper
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                buttonColor={item?.color}
                              >
                                {item?.text || 'Make Payment'}
                              </CustomButtonWrapper>
                            ) : item.itemId ===
                              APP_CONFIG.form_fields.standard_fields.datePicker
                                .id ? (
                              <DatePickerPreview
                                placeholder={item?.label || ''}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                item={item}
                                key={item.id}
                              />
                            ) : (
                              <SignatureAndInitialsPreviewChip
                                item={item}
                                key={item.id}
                                x={item?.placeholder?.x}
                                y={item?.placeholder?.y}
                                showColor
                              />
                            )),
                        )}
                    </div>
                  </PDFPageOverlay>
                ))}
              </DocumentOverlay>

              {fileLinkBottom &&
              !isPreview &&
              !isTemplate?.status &&
              pdfIndividualPageDetails?.[0]?.width ? (
                <SwitchPageLayer
                  scalingFactor={overlayScalingFactor}
                  file={fileLinkBottom}
                  width={
                    pdfIndividualPageDetails?.[0]?.width * overlayScalingFactor
                  }
                ></SwitchPageLayer>
              ) : null}
            </ScaledContainer>
          </div>
        </div>
      </RelativeContainer>
    </div>
  );
};

export default PreviewPDF;
